import { Component, EventEmitter, HostBinding, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { Project } from 'src/app/core/objects/project';
import { ProjectService } from 'src/app/core/services/project.service';
import { Load } from '../../visualisation/load';
import { FlipType } from './../../../core/enums/FlipType';
import { ColorsService } from './../../../core/services/colors.service';
import { ProjectApiService } from './../../../core/services/project-api.service';
import { FADE_IN_OUT_ANIMATION } from './../../../shared/animations/animations';
import { MainView } from '../../visualisation/main-view';
import { Vector3 } from 'three';
import { switchMap, take } from 'rxjs/operators';

export interface rotationData {
  yRotation: number;
  edgeRotation: number;
  objectName: string;
  currentWorkSpaceId: number;
  target: {
    workSpaceId: number;
    position: Vector3;
    stopId: number;
  };
  currentStopId: number;
}

@Component({
    selector: 'gl-load-context-menu',
    templateUrl: './load-context-menu.component.html',
    styleUrls: ['./load-context-menu.component.scss'],
    animations: [FADE_IN_OUT_ANIMATION],
    standalone: false
})
export class LoadContextMenuComponent implements OnInit, OnDestroy {
  @HostBinding('@show') public show;
  @Input() public load: Load;
  @Input() public loadOriginal: Load;
  @Input() public visualisation: MainView;
  public project: Project;
  @Output() public changeFloorAction: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public closeContextMenu = new EventEmitter<any>();
  private currentWorkSpace: number;
  public isPallet = false;
  public isVarioFloor = false;
  // private rotationX = 0;
  // private rotationY = 0;
  // private rotationStep = Math.PI / 36;
  // private originalPosition: Vector3;
  public isShow = true;
  public isRotated = false;

  constructor(
    private colorsService: ColorsService,
    private projectApiService: ProjectApiService,
    private projectService: ProjectService,
  ) {
    this.projectService.project.subscribe((project) => {
      this.project = project;
    });

    this.projectService.currentWorkSpaceIndex.subscribe((currentWorkSpace) => {
      this.currentWorkSpace = currentWorkSpace;
      const varioFloor =
        this.project.workSpaces[this.currentWorkSpace].cargoSpaces[0].extraFeatures
          .varioTrailerFloorHeight ||
        this.project.workSpaces[this.currentWorkSpace].cargoSpaces[0].extraFeatures
          .varioSemiTrailerFloorHeight;
      this.isVarioFloor = varioFloor !== undefined && varioFloor !== null;
    });
  }

  public getLoadIcon(): string {
    return this.colorsService.getDrawedCircle(this.load.color);
  }

  public areLoadBaseDimensionsSame(): boolean {
    return this.load.width === this.load.length;
  }

  public delete(): void {
    const loadPosition: Vector3 = new Vector3(
      this.loadOriginal.oldPosition.x / 100,
      this.loadOriginal.oldPosition.y / 100,
      this.loadOriginal.oldPosition.z / 100,
    );
    this.projectService.currentStopIndex
      .pipe(
        take(1),
        switchMap((currentStopIndex: number) => {
          return this.projectApiService.deleteLoad({
            currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
            target: {
              workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
              position: loadPosition,
              stopId: currentStopIndex !== undefined ? currentStopIndex + 1 : undefined,
            },
          });
        }),
      )
      .subscribe(() => {
        this.successHandler();
      });
  }

  public flipShorter() {
    this.projectService.currentStopIndex
      .pipe(
        take(1),
        switchMap((currentStopIndex: number) => {
          return this.projectApiService.flipLoad({
            currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
            target: {
              workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
              position: this.load.position,
              stopId:
                currentStopIndex >= 0
                  ? this.project.workSpaces[this.currentWorkSpace].stops[currentStopIndex].id
                  : undefined,
            },
            flipType: FlipType.Shorter,
          });
        }),
      )
      .subscribe(() => this.successHandler());
  }

  public flipLonger() {
    this.projectService.currentStopIndex
      .pipe(
        take(1),
        switchMap((currentStopIndex: number) => {
          return this.projectApiService.flipLoad({
            currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
            target: {
              workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
              position: this.load.position,
              stopId:
                currentStopIndex >= 0
                  ? this.project.workSpaces[this.currentWorkSpace].stops[currentStopIndex].id
                  : undefined,
            },
            flipType: FlipType.Longer,
          });
        }),
      )
      .subscribe(() => this.successHandler());
  }

  public mouseRotateY(): void {
    this.closeContextMenu.emit('mouseRotateY');
  }

  public mouseRotateX(): void {
    this.closeContextMenu.emit('mouseRotateX');
  }

  public flip() {
    this.projectService.currentStopIndex
      .pipe(
        take(1),
        switchMap((currentStopIndex: number) => {
          return this.projectApiService.flipLoad({
            currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
            target: {
              workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
              position: this.load.position,
              stopId:
                currentStopIndex >= 0
                  ? this.project.workSpaces[this.currentWorkSpace].stops[currentStopIndex].id
                  : undefined,
            },
            flipType: FlipType.Unimportant,
          });
        }),
      )
      .subscribe(() => this.successHandler());
  }

  public changeFloor(): void {
    this.projectService.currentStopIndex
      .pipe(
        take(1),
        switchMap((currentStopIndex: number) => {
          return this.projectApiService.changeFloor({
            currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
            target: {
              workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
              position: this.load.position,
              stopId:
                currentStopIndex >= 0
                  ? this.project.workSpaces[this.currentWorkSpace].stops[currentStopIndex].id
                  : undefined,
            },
          });
        }),
      )
      .subscribe(() => this.successHandler());
  }

  public rotate() {
    this.projectService.currentStopIndex
      .pipe(
        take(1),
        switchMap((currentStopIndex: number) => {
          return this.projectApiService.editLoad({
            currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
            target: {
              workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
              position: this.load.position,
              stopId:
                currentStopIndex >= 0
                  ? this.project.workSpaces[this.currentWorkSpace].stops[currentStopIndex].id
                  : undefined,
            },
            changes: {
              reverted: true,
            },
          });
        }),
      )
      .subscribe(() => this.successHandler());
  }

  public changeStacking(): void {
    this.projectService.currentStopIndex
      .pipe(
        take(1),
        switchMap((currentStopIndex: number) => {
          return this.projectApiService.editLoad({
            currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
            target: {
              workSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
              position: this.load.position,
              stopId: currentStopIndex
                ? this.project.workSpaces[this.currentWorkSpace].stops[currentStopIndex].id
                : undefined,
            },
            changes: {
              stacking: !this.load.stacking,
            },
          });
        }),
      )
      .subscribe(() => this.successHandler());
  }

  private successHandler() {
    this.closeContextMenu.emit('close');
  }

  ngOnInit(): void {
    this.isPallet = this.load.loadType === 3;
    // this.rotationY = this.loadOriginal.rotation.y;
    // this.rotationX = this.loadOriginal.rotation.x;
    // this.originalPosition = { ...this.loadOriginal.oldPosition } as Vector3;
    this.isRotated = !!this.loadOriginal.yRotation || !!this.loadOriginal.edgeRotation;
  }

  ngOnDestroy(): void {
    const rotatingTool =
      this.visualisation.renderingContext.scene.getObjectByName('rotationToolGroup');
    if (rotatingTool) {
      this.visualisation.renderingContext.scene.remove(rotatingTool);
    }
  }
}
